import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import Modal from 'react-modal';
import MainCard from 'components/MainCard';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import Swal from 'sweetalert2';
import { TailSpin } from 'react-loader-spinner';
import { MdCloudUpload } from 'react-icons/md';
import { Edit, Trash } from 'lucide-react';
import DataTable from 'react-data-table-component';
import { ToastContainer, toast } from 'react-toastify';
import { FiSearch } from 'react-icons/fi';
import BackButton from 'components/BackButton';
import { baseURLFinanceVendor } from 'config/apiConfig';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { FcClearFilters } from "react-icons/fc";
import { customStyles } from 'utils/customStyleDataTable';

Modal.setAppElement('#root');

const ServiceTypSubTypUploader = () => {
    const [file, setFile] = useState(null);
    const [loadingData, setLoadingData] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);
    const [serviceTypeSubTypeData, setServiceTypeSubTypeData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showUploader, setShowUploader] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editFormData, setEditFormData] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [selectedServiceType, setSelectedServiceType] = useState('');

    const uniqueServiceTypes = useMemo(() => {
        return [...new Set(serviceTypeSubTypeData.map(item => item.serviceType).filter(Boolean))];
    }, [serviceTypeSubTypeData]);

    const isFilterActive = selectedServiceType || searchTerm;

    const allowedTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel.sheet.macroEnabled.12',
        'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
        'application/vnd.ms-excel.template.macroEnabled.12',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
        'text/csv',
    ];

    const maxSize = 10 * 1024 * 1024;

    const handleFile = (selectedFile) => {
        if (!selectedFile) return;

        if (!allowedTypes.includes(selectedFile.type)) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid File Type',
                text: 'Please upload a valid Excel file (.xls, .xlsx, .xlsm, .xlsb, .xltx, .xltm, .csv)',
            });
            return;
        }

        if (selectedFile.size > maxSize) {
            Swal.fire({
                icon: 'error',
                title: 'File Too Large',
                text: 'File size should not exceed 10MB!',
            });
            return;
        }

        setFile(selectedFile);
    };

    const handleExcelUploadChange = (event) => {
        const selectedFile = event.target.files[0];
        handleFile(selectedFile);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragEnter = (event) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setIsDragging(false);
        const droppedFile = event.dataTransfer.files[0];
        handleFile(droppedFile);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const fetchServiceTypeSubType = async () => {
        try {
            setIsLoading(true);
            const token = localStorage.getItem("token");

            const res = await axios.get(
                `${baseURLFinanceVendor}getServiceTypeSubType`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (res && res.data) {
                setServiceTypeSubTypeData(res?.data?.data || res?.data || []);
            }
        } catch (error) {
            toast.error('Error while fetching data!');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchServiceTypeSubType();
    }, []);

    const handleExcelUpload = async () => {
        if (!file) {
            Swal.fire({
                icon: 'warning',
                title: 'No File Selected',
                text: 'Please select a file before uploading!',
            });
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        setLoadingData(true);

        try {
            const token = localStorage.getItem("token");

            const response = await axios.post(
                `${baseURLFinanceVendor}bulkUploadServiceTypeSubType`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response) {
                await fetchServiceTypeSubType();
                setShowUploader(false);
                Swal.fire({
                    icon: 'success',
                    title: 'Upload Successful',
                    text: response.data.message || 'Service Type / Sub-Type uploaded successfully!',
                    confirmButtonText: 'OK',
                });

                setFile(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Upload Failed',
                text: error.response?.data?.message || 'Something went wrong. Please try again!',
            });
        } finally {
            setLoadingData(false);
        }
    };

    const handleDownload = () => {
        const today = new Date();

        const formattedDate = `${String(today.getDate()).padStart(2, "0")}-${String(
            today.getMonth() + 1
        ).padStart(2, "0")}-${today.getFullYear()}`;

        const link = document.createElement("a");

        link.href = "/templates/Service_Type_Sub_Type_Template.xlsx";
        link.download = `Service_Type_Sub_Type_Template_${formattedDate}.xlsx`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleEdit = useCallback((row) => {
        setEditFormData({
            id: row.id,
            serviceType: row.serviceType || '',
            serviceSubType: row.serviceSubType || '',
            type: 'edit'
        });
        setShowEditModal(true);
        setShowAddModal(false);
    }, []);

    const handleAdd = useCallback(() => {
        setEditFormData({
            id: 0,
            serviceType: '',
            serviceSubType: '',
            type: 'add'
        });
        setShowAddModal(true);
        setShowEditModal(false);
    }, []);

    const handleEditChange = useCallback((e) => {
        const { name, value } = e.target;
        setEditFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    }, []);

    const handleSaveEdit = async () => {
        if (!editFormData) return;

        const payload = {
            id: Number(editFormData.id),
            serviceType: editFormData.serviceType || "",
            serviceSubType: editFormData.serviceSubType || "",
        };

        setLoadingData(true);

        try {
            const token = localStorage.getItem("token");

            const res = await axios.post(
                `${baseURLFinanceVendor}updateServiceTypeSubType`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            if (res.status === 200 || res.status === 201) {
                await fetchServiceTypeSubType();
                setShowEditModal(false);
                setEditFormData(null);
                Swal.fire({
                    icon: 'success',
                    title: 'Update Successful',
                    text: 'Service Type / Sub-Type updated successfully!',
                });
            }
        } catch (error) {
            console.error("Edit Error:", error.response?.data);
            Swal.fire({
                icon: 'error',
                title: 'Update Failed',
                text: error.response?.data?.errors?.model?.[0] ||
                    error.response?.data?.title ||
                    error.response?.data?.message ||
                    'Something went wrong!',
            });
        } finally {
            setLoadingData(false);
        }
    };

    const handleSaveAdd = async () => {
        if (!editFormData) return;

        const payload = {
            serviceType: editFormData.serviceType || "",
            serviceSubType: editFormData.serviceSubType || "",
        };

        setLoadingData(true);

        try {
            const token = localStorage.getItem("token");

            const res = await axios.post(
                `${baseURLFinanceVendor}AddServiceTypeSubType`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            if (res.status === 200 || res.status === 201) {
                await fetchServiceTypeSubType();
                setShowAddModal(false);
                setEditFormData(null);
                Swal.fire({
                    icon: 'success',
                    title: 'Add Successful',
                    text: 'Service Type / Sub-Type added successfully!',
                });
            }
        } catch (error) {
            console.error("Add Error:", error.response?.data);
            Swal.fire({
                icon: 'error',
                title: 'Add Failed',
                text: error.response?.data?.title ||
                    error.response?.data?.message ||
                    'Something went wrong!',
            });
        } finally {
            setLoadingData(false);
        }
    };

    const handleCancelEdit = useCallback(() => {
        setShowEditModal(false);
        setShowAddModal(false);
        setEditFormData(null);
    }, []);

    const handleDelete = async (row) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    setLoadingData(true);

                    const token = localStorage.getItem("token");

                    await axios.post(
                        `${baseURLFinanceVendor}deleteServiceTypeSubType`,
                        { id: row.id },
                        {
                            headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json"
                            },
                        }
                    );

                    await fetchServiceTypeSubType();

                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'Record deleted successfully',
                    });

                } catch (error) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Delete Failed',
                        text: error.response?.data?.message || 'Something went wrong!',
                    });
                } finally {
                    setLoadingData(false);
                }
            }
        });
    };

    const columns = useMemo(
        () => [
            {
                name: 'SN',
                selector: (row, index) => (currentPage - 1) * rowsPerPage + (index + 1),
                width: '70px',
            },
            {
                name: 'Service Type',
                selector: (row) => row.serviceType || '',
                sortable: true,
                grow: 1.5
            },
            {
                name: 'Service Sub-Type',
                selector: (row) => row.serviceSubType || '',
                sortable: true,
                grow: 2
            },
            {
                name: 'Actions',
                cell: (row) => (
                    <div className="flex gap-2">
                        <button
                            onClick={() => handleEdit(row)}
                            className="p-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition"
                            title="Edit"
                        >
                            <Edit className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => handleDelete(row)}
                            className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition"
                            title="Delete"
                        >
                            <Trash className="w-5 h-5" />
                        </button>
                    </div>
                ),
                width: '120px',
            },
        ],
        [currentPage, rowsPerPage, handleEdit]
    );

    const filteredData = useMemo(() => {
        const searchLower = searchTerm.toLowerCase();

        return serviceTypeSubTypeData.filter((item) => {
            const matchesSearch =
                item.serviceType?.toLowerCase().includes(searchLower) ||
                item.serviceSubType?.toLowerCase().includes(searchLower);

            const matchesServiceType = !selectedServiceType || item.serviceType === selectedServiceType;

            return matchesSearch && matchesServiceType;
        });
    }, [serviceTypeSubTypeData, searchTerm, selectedServiceType]);

    const modalStyles = {
        overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 50,
        },
        content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#fff',
            padding: '24px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '80vh',
            overflowY: 'auto',
            margin: '16px',
        },
    };

    return (
        <div>
            <MainCard>
                <div className="flex justify-between gap-4">
                    <BackButton />
                    <div className="flex justify-end items-center gap-[1.5rem]">
                        <button
                            onClick={handleDownload}
                            className="flex items-center px-4 py-2 border border-purple-700 text-purple-700 rounded-lg 
                                   hover:bg-purple-50 transition-all duration-300"
                        >
                            <FileDownloadOutlinedIcon className="mr-2" /> Download Template
                        </button>
                        <button
                            onClick={() => setShowUploader(!showUploader)}
                            className="flex items-center px-4 py-2 bg-purple-700 text-white rounded-lg 
                                   hover:bg-purple-800 transition-all duration-300"
                        >
                            <CloudUploadIcon className="mr-2" /> {showUploader ? 'Hide Uploader' : 'Show Uploader'}
                        </button>
                        <button
                            onClick={handleAdd}
                            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg 
                                   hover:bg-green-700 transition-all duration-300"
                        >
                            <AddIcon className="mr-2" /> Add New
                        </button>
                    </div>
                </div>

                {showUploader && (
                    <div
                        className={`flex flex-col items-center justify-center pb-4 sm:pb-4 mt-4
                                    border-2 border-dashed rounded-xl transition-all duration-300 text-center
                                    ${isDragging ? 'border-purple-800 bg-purple-200' : 'border-purple-600 bg-purple-50 hover:bg-purple-100'}`}
                        onDragOver={handleDragOver}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <label
                            htmlFor="file-upload"
                            className="flex flex-col items-center cursor-pointer text-purple-700 hover:text-purple-800 transition-all"
                        >
                            <MdCloudUpload size={60} className="mb-2 hover:scale-110 transition-transform duration-300" />
                            <span className="text-base sm:text-lg font-semibold">
                                {isDragging ? 'Drop File Here' : 'Choose File'}
                            </span>
                            <span className="text-xs text-gray-500">(Excel or CSV - .xls, .xlsx, .csv)</span>
                        </label>

                        <input
                            id="file-upload"
                            type="file"
                            accept=".xls,.xlsx,.xlsm,.xlsb,.xlt,.xltx,.xltm,.csv"
                            onChange={handleExcelUploadChange}
                            className="hidden"
                            ref={fileInputRef}
                        />

                        {file && (
                            <p className="text-sm text-gray-700 mt-4 break-words px-2">
                                <span className="font-medium text-purple-700">Selected:</span> {file.name}
                            </p>
                        )}

                        <button
                            onClick={handleExcelUpload}
                            disabled={loadingData}
                            className={`mt-5 flex items-center px-6 py-2 rounded-lg font-medium transition-all duration-300 shadow-md
                                        ${loadingData ? 'bg-purple-400 cursor-not-allowed' : 'bg-purple-700 hover:bg-purple-800 text-white'}`}
                        >
                            <CloudUploadIcon className="mr-2" /> {loadingData ? 'Uploading...' : 'Upload Service Type / Sub-Type'}
                        </button>
                    </div>
                )}

                <div className='flex justify-between items-center gap-3 mt-4'>
                    <div className="flex flex-wrap items-center gap-4 my-[0.5rem]">
                        <div className="relative">
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search by Service Type or Sub-Type"
                                className="peer pl-10 pr-4 py-2 w-80 md:w-96 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#6C5DD3]"
                            />
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 peer-focus:text-[#6C5DD3]">
                                <FiSearch className="" />
                            </span>
                        </div>
                    </div>

                    <div className='flex justify-start gap-3 items-center'>
                        <div className='flex justify-start gap-2 items-center'>
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel>Service Type</InputLabel>
                                <Select
                                    value={selectedServiceType}
                                    label="Service Type"
                                    onChange={(e) => setSelectedServiceType(e.target.value)}
                                >
                                    <MenuItem value="">All Service Types</MenuItem>
                                    {uniqueServiceTypes.map(type => (
                                        <MenuItem key={type} value={type}>{type}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>

                        <button
                            onClick={() => {
                                setSelectedServiceType('');
                                setSearchTerm('');
                            }}
                            disabled={!isFilterActive}
                            className={`p-[0.3rem] text-[1.4rem] border rounded transition-all duration-300 ease-in-out
                                ${isFilterActive
                                    ? 'text-gray-600 border-gray-300 hover:text-red-600 hover:bg-gray-200 hover:scale-110 cursor-pointer'
                                    : 'text-gray-300 border-gray-200 cursor-not-allowed'
                                }`}
                            title="Reset Filters"
                        >
                            <FcClearFilters />
                        </button>
                    </div>
                </div>

                <div className="overflow-auto rounded-[0.3rem] border border-gray-200 shadow-sm mt-2">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center h-[300px] gap-3">
                            <TailSpin
                                visible={true}
                                height="50"
                                width="50"
                                color="#7E22CE"
                                ariaLabel="tail-spin-loading"
                                radius="1"
                            />
                            <p className="text-sm font-medium text-gray-600">
                                Fetching Service Type / Sub-Type data...
                            </p>
                        </div>
                    ) : (
                        <DataTable
                            columns={columns}
                            data={filteredData}
                            customStyles={customStyles}
                            pagination
                            paginationServer={false}
                            paginationTotalRows={filteredData.length}
                            highlightOnHover
                            responsive
                            onChangePage={(page) => setCurrentPage(page)}
                            onChangeRowsPerPage={(newPerPage, page) => {
                                setRowsPerPage(newPerPage);
                                setCurrentPage(page);
                            }}
                        />
                    )}
                </div>

                {loadingData && (
                    <div className="fixed inset-0 bg-white bg-opacity-80 z-999 flex justify-center items-center">
                        <TailSpin height="60" width="60" color="#6C5DD3" ariaLabel="loading" />
                    </div>
                )}
            </MainCard>

            <Modal
                isOpen={showEditModal || showAddModal}
                onRequestClose={handleCancelEdit}
                style={modalStyles}
                contentLabel={showEditModal ? 'Edit Service Type / Sub-Type' : 'Add New Service Type / Sub-Type'}
            >
                {editFormData && (
                    <div>
                        <h2 className="text-xl font-bold mb-4 text-purple-700">
                            {showEditModal ? 'Edit Service Type / Sub-Type' : 'Add New Service Type / Sub-Type'}
                        </h2>

                        <form className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Service Type</label>
                                <input
                                    type="text"
                                    name="serviceType"
                                    value={editFormData.serviceType}
                                    onChange={handleEditChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-400"
                                    placeholder="e.g. Damage Insurance"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Service Sub-Type</label>
                                <input
                                    type="text"
                                    name="serviceSubType"
                                    value={editFormData.serviceSubType}
                                    onChange={handleEditChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-400"
                                    placeholder="e.g. Screen/Display Damage"
                                    required
                                />
                            </div>
                        </form>

                        <div className="flex justify-end gap-4 mt-6">
                            <button
                                onClick={showEditModal ? handleSaveEdit : handleSaveAdd}
                                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-all"
                            >
                                Save
                            </button>
                            <button
                                onClick={handleCancelEdit}
                                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-all"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
            <ToastContainer />
        </div>
    );
};

export default ServiceTypSubTypUploader;